import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calculator, TrendingUp } from 'lucide-react';
import { LOGOS } from './AssetPlaceholder';

const ROICalculator = ({ isOpen, onClose }) => {
  const [providers, setProviders] = useState(3);
  const [patientsPerDay, setPatientsPerDay] = useState(20);
  const [selectedApps, setSelectedApps] = useState({ lumina: true, revq: true, echo: true });

  const toggleApp = useCallback((app) => {
    setSelectedApps(prev => ({ ...prev, [app]: !prev[app] }));
  }, []);

  // ROI calculation model
  const luminaSavings = selectedApps.lumina ? providers * patientsPerDay * 22 * 8 : 0; // $8 per note in time savings
  const revqSavings = selectedApps.revq ? providers * patientsPerDay * 22 * 12 : 0; // $12 per claim revenue lift
  const echoSavings = selectedApps.echo ? providers * patientsPerDay * 22 * 3 : 0; // $3 per appt in fill rate
  const totalAnnual = (luminaSavings + revqSavings + echoSavings) * 12;
  const monthlyCreditCost = providers * patientsPerDay * 22 * (selectedApps.lumina ? 2 : 0) + (selectedApps.revq ? providers * patientsPerDay * 22 * 3 : 0) + (selectedApps.echo ? providers * 22 * 5 : 0);
  const monthlyROI = luminaSavings + revqSavings + echoSavings - monthlyCreditCost;

  const formatCurrency = (val) => {
    if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
    if (val >= 1000) return `$${(val / 1000).toFixed(0)}k`;
    return `$${val.toFixed(0)}`;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <motion.div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
          
          {/* Modal */}
          <motion.div
            className="relative w-full max-w-2xl rounded-2xl overflow-hidden"
            style={{ background: '#111111', border: '1px solid rgba(255,255,255,0.08)' }}
            initial={{ scale: 0.95, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            data-testid="roi-calculator-modal"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/[0.06]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                  <Calculator className="w-5 h-5 text-white/60" />
                </div>
                <div>
                  <h3 className="text-white text-lg font-medium">ROI Projector</h3>
                  <p className="text-aria-text-muted text-xs">Estimate your return with AriaOne</p>
                </div>
              </div>
              <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors" data-testid="roi-close">
                <X className="w-4 h-4 text-white/60" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-6">
              {/* Inputs */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-white/60 text-xs mb-2 block">Number of Providers</label>
                  <input
                    type="range" min="1" max="20" value={providers}
                    onChange={(e) => setProviders(Number(e.target.value))}
                    className="w-full accent-[#ffffff]"
                    data-testid="roi-providers-slider"
                  />
                  <div className="flex justify-between text-xs mt-1">
                    <span className="text-aria-text-muted">1</span>
                    <span className="text-white font-medium">{providers}</span>
                    <span className="text-aria-text-muted">20</span>
                  </div>
                </div>
                <div>
                  <label className="text-white/60 text-xs mb-2 block">Patients per Provider/Day</label>
                  <input
                    type="range" min="5" max="40" value={patientsPerDay}
                    onChange={(e) => setPatientsPerDay(Number(e.target.value))}
                    className="w-full accent-[#ffffff]"
                    data-testid="roi-patients-slider"
                  />
                  <div className="flex justify-between text-xs mt-1">
                    <span className="text-aria-text-muted">5</span>
                    <span className="text-white font-medium">{patientsPerDay}</span>
                    <span className="text-aria-text-muted">40</span>
                  </div>
                </div>
              </div>

              {/* App selection */}
              <div>
                <label className="text-white/60 text-xs mb-3 block">Apps Included</label>
                <div className="flex gap-3">
                  {[
                    { key: 'lumina', logo: LOGOS.lumina, label: 'Lumina' },
                    { key: 'revq', logo: LOGOS.revq, label: 'RevQ' },
                    { key: 'echo', logo: LOGOS.echo, label: 'Echo' },
                  ].map((app) => (
                    <button
                      key={app.key}
                      onClick={() => toggleApp(app.key)}
                      className={`flex-1 flex items-center gap-2 px-4 py-3 rounded-xl border transition-all ${
                        selectedApps[app.key]
                          ? 'border-white/30 bg-white/5'
                          : 'border-white/[0.06] bg-white/[0.02] opacity-50'
                      }`}
                      data-testid={`roi-toggle-${app.key}`}
                    >
                      <img src={app.logo} alt={app.label} className="h-4 object-contain" />
                    </button>
                  ))}
                </div>
              </div>

              {/* Results */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Monthly Savings', value: formatCurrency(luminaSavings + revqSavings + echoSavings), color: '#ffffff' },
                  { label: 'Monthly Cost', value: formatCurrency(monthlyCreditCost), color: '#ff5f57' },
                  { label: 'Net Monthly ROI', value: formatCurrency(monthlyROI), color: '#28c840' },
                ].map((stat) => (
                  <div key={stat.label} className="rounded-xl p-4 text-center" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div className="text-2xl font-medium" style={{ color: stat.color }}>{stat.value}</div>
                    <div className="text-aria-text-muted text-[10px] mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* Annual projection */}
              <div className="rounded-xl p-5 text-center" style={{ background: 'linear-gradient(135deg, rgba(107,92,231,0.1), rgba(40,200,64,0.05))', border: '1px solid rgba(107,92,231,0.15)' }}>
                <div className="flex items-center justify-center gap-2 mb-1">
                  <TrendingUp className="w-4 h-4 text-green-400" />
                  <span className="text-aria-text-muted text-xs">Projected Annual Impact</span>
                </div>
                <div className="text-3xl text-white font-medium">{formatCurrency(totalAnnual)}</div>
                <p className="text-aria-text-muted text-[11px] mt-2 max-w-sm mx-auto">
                  Based on {providers} providers seeing {patientsPerDay} patients/day, 22 working days/month
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ROICalculator;
